import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import flatten from 'lodash.flatten';
import cloneDeep from 'lodash.clonedeep';
import { EventData, Layer, LngLatLike, Map as MapBoxMap } from 'mapbox-gl';
import { FeatureCollection } from 'geojson';

import { dataLayer } from '@weatherbug/common';
import * as constants from './constants';
import hurricaneIcons from './assets/hurricane/index';
import { featureMap, featureMappingService } from './constants';
import { 
    CurrentHurricane, 
    CurrentTrackPoint, 
    Feature, 
    GeoJsonLayerPoint, 
    HurricaneLegend, 
    HurricaneLegendColor, 
    HurricaneLegendColorStop, 
    HurricanesGeoJSON, 
    IStdParameters, 
    IStoreState 
} from '../utilities/interfaces';

const { getHurricaneByLatLon, getHurricaneLegend, selectHurricaneTrackPoint, selectHurricane } = dataLayer.actions;
const getHurricaneUriPath = '/api/hurricanes/v1';
const getLegendUriPath = '/giv/presentation/legenddata';
const mapSourceId = 'hurricane';

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { hurricane } = storeState;
    if (!hurricane) {
        return;
    }

    return {
        hurricanesGeoJson: hurricane.hurricanesGeoJson,
        hurricanes: hurricane.hurricanes,
        currentTrackPointGeoJson: hurricane.currentTrackPointGeoJson,
        currentTrackPoint: hurricane.currentTrackPoint,
        currentHurricaneIndex: hurricane.currentHurricaneIndex,
        currentHurricane: hurricane.currentHurricane,
        legendData: hurricane.legend
    };
};

const mapDispatchToProps = {
    getHurricaneByLatLon,
    getHurricaneLegend,
    selectHurricaneTrackPoint,
    selectHurricane
};

interface HurricaneLayerType {
    map: InstanceType<typeof MapBoxMap>;
    latitude: number;
    longitude: number;
    hurricaneBaseUrl: string;
    legendBaseUrl: string;
    authId: string;
    secret: string;
    stdApiParameters: IStdParameters;
    getHurricaneByLatLon: (
        latitude: number,
        longitude: number,
        isCached: boolean,
        hurricaneBaseUrl: string,
        getHurricaneUriPath: string,
        authId: string,
        secret: string,
        stdApiParameters: IStdParameters,
        unitType: string,
        verbose: boolean
    ) => void;
    getHurricaneLegend: (
        isCached: boolean, 
        baseUrl: string, 
        path: typeof getLegendUriPath, 
        authId: string, 
        secret: string, 
        stdParameters: IStdParameters
    ) => void;
    selectHurricaneTrackPoint: (feature: Feature) => void;
    selectHurricane: (id: number) => void;
    legendData?: HurricaneLegend;
    renderLegend?: (legendColors?: string[], legendLabels?: string[]) => ReactNode;
    unitType: string;
    hurricanesGeoJson: HurricanesGeoJSON;
    hurricanes: CurrentHurricane[];
    currentTrackPointGeoJson: Feature;
    currentTrackPoint: CurrentTrackPoint;
    currentHurricaneIndex: number;
    currentHurricane: CurrentHurricane;
    children: ReactNode;
}

export const HurricaneLayer = connect(mapStoreStateToProps, mapDispatchToProps)(({
    map,
    latitude,
    longitude,
    hurricaneBaseUrl,
    legendBaseUrl,
    authId,
    secret,
    stdApiParameters,
    getHurricaneByLatLon,
    getHurricaneLegend,
    selectHurricaneTrackPoint,
    selectHurricane,
    legendData,
    renderLegend,
    unitType,
    hurricanesGeoJson,
    hurricanes,
    currentTrackPointGeoJson,
    currentTrackPoint,
    currentHurricaneIndex,
    currentHurricane,
    children
}: HurricaneLayerType) => {
    const dataLoaded = useRef(false);
    const featuresLoaded = useRef(false);
    const [ imagesLoaded, setImagesLoaded ] = useState(false);
    const legendColors = useRef<string[]>();
    const legendLabels = useRef<string[]>();
    const previousLatitude = useRef<number>(latitude);
    const previousLongitude = useRef<number>(longitude);
    const previousTrackPointGeoJson = useRef<Feature>(currentTrackPointGeoJson);

    const loadInitialData = (override?: boolean) => {
        if ((!dataLoaded || override)
            && latitude
            && longitude
            && hurricaneBaseUrl
            && legendBaseUrl
            && authId
            && secret) {
            dataLoaded.current = true;

            const isCached = false;
            const verbose = false;
            getHurricaneByLatLon(
                latitude,
                longitude,
                isCached,
                hurricaneBaseUrl,
                getHurricaneUriPath,
                authId,
                secret,
                stdApiParameters,
                unitType || constants.unitSystems.english,
                verbose
            );
            if (!legendData || !legendData.val) {
                getHurricaneLegend(false, legendBaseUrl, getLegendUriPath, authId, secret, stdApiParameters);
            }
        }
    };

    useEffect(() => {
        if (latitude && longitude && currentTrackPointGeoJson && map) {
            dataLoaded.current = false;
            loadInitialData();
            const locationChanged = previousLatitude?.current !== latitude || previousLongitude?.current !== longitude;
            const hurricaneFeatures = hurricanesGeoJson ? hurricanesGeoJson?.features : [];

            if (locationChanged && dataLoaded?.current) {
                previousLatitude.current = latitude;
                previousLongitude.current = longitude;
                map?.easeTo({ center: [longitude, latitude] });

                if (dataLoaded?.current && featuresLoaded?.current) {
                    map?.on('style.load', function() {
                        if (map.isStyleLoaded()) {
                            const layersToRemove = map?.getStyle()?.layers?.filter((x: Layer) => x?.source === mapSourceId);
                            layersToRemove?.forEach((x: Layer) => map?.removeLayer(x?.id));
                        }
                    });
                  
                    map?.removeSource(mapSourceId);
                    featuresLoaded.current = false;
                    dataLoaded.current = false;
                    loadInitialData(true);
                }
            }

            if (dataLoaded?.current 
                && previousTrackPointGeoJson?.current?.properties?.index !== currentTrackPointGeoJson?.properties?.index
                || previousTrackPointGeoJson?.current?.properties?.id !== currentTrackPointGeoJson?.properties?.id) {
                previousTrackPointGeoJson.current = currentTrackPointGeoJson;
                selectFeature({ features: [currentTrackPointGeoJson] });
            }

            if (!featuresLoaded?.current && hurricaneFeatures?.length > 0) {
                if (map?.loaded()) {
                    addHurricaneSourceAndLayer();
                } else {
                    map?.on('load', () => {
                        addHurricaneSourceAndLayer();
                    });
                }

                featuresLoaded.current = true;
                dataLoaded.current = true;
            }

            if (!legendColors?.current && legendData?.val && dataLoaded?.current) {
                const legendDataVal = legendData?.val;
                if (legendDataVal?.layerComponents?.length > 0) {
                    const currentLayerComponent = legendDataVal?.layerComponents[0];
                    if (currentLayerComponent?.legendData?.colors && currentLayerComponent?.legendData?.colors?.length > 0) {
                        const colors = currentLayerComponent?.legendData?.colors?.find((x: HurricaneLegendColor) => x.unitType === unitType);
                        const scopedLegendLabels = colors?.colorStops?.map((x: HurricaneLegendColorStop) => {
                            return x?.label;
                        });
                        const scopedLegendColors = colors?.colorStops?.map((x: HurricaneLegendColorStop) => {
                            return x?.color;
                        });

                        if (scopedLegendColors) {
                            legendColors.current = scopedLegendColors;
                            legendLabels.current = scopedLegendLabels;
                        }
                    }
                }
            }
        }
    }, [latitude, longitude, currentTrackPointGeoJson]);

    const addIconImages = async() => {
        const iconNames = Object?.keys(hurricaneIcons);
        const promises: Promise<void>[] = [];

        iconNames?.forEach(iconName => {
            promises?.push(new Promise(resolve => {
                map?.loadImage(hurricaneIcons[iconName], (error: Error | undefined, result: HTMLImageElement | ImageBitmap | undefined) => {
                    if (error) {
                        console.log('Error loading image: ' + iconName);
                        console.log(error);
                        return;
                    }
                    if (result) {
                        map?.addImage(iconName, result);
                    }
                    
                    resolve();
                });
            }));
        });
        
        await Promise?.all(promises);
        setImagesLoaded(true);
    };

    const addHurricaneSourceAndLayer = async() => {
        if (!imagesLoaded) {
            await addIconImages();
        }

        map.addSource(mapSourceId, {
            type: 'geojson',
            data: hurricanesGeoJson as FeatureCollection
        });

        const currentTrackPointIndex = currentTrackPointGeoJson.properties.index;
        map.easeTo({ center: currentTrackPointGeoJson.geometry.coordinates as LngLatLike });

        const pointNotSelectedCondition = ['!=', 'index', currentTrackPointIndex];
        const pointSelectedCondition = ['==', 'index', currentTrackPointIndex];
        const featureMapping = featureMappingService(pointNotSelectedCondition, pointSelectedCondition);
        const hurricaneNotSelected = ['!=', 'id', currentHurricane.id];
        const hurricaneSelected = ['==', 'id', currentHurricane.id];
        const unselectedFeatureMapping = cloneDeep(featureMapping);

        //Map the unselected hurricane features
        unselectedFeatureMapping.forEach((unselectedFeature: featureMap) => {
            const id = 'unselected' + unselectedFeature.id;
            const { type, filter, style, layout } = unselectedFeature;
            filter.push(hurricaneNotSelected);
            const paint = cloneDeep(style);
            if (type === 'line') {
                paint['line-opacity'] = Math.abs(+paint['line-opacity'] || 1) / 2;
                map.addLayer({
                    id,
                    type,
                    filter,
                    paint,
                    source: mapSourceId
                });
            } else if (type === 'fill') {
                paint['fill-opacity'] = Math.abs(+(paint['fill-opacity'] || 1) / 2);
                map.addLayer({
                    id,
                    type,
                    filter,
                    paint,
                    source: mapSourceId
                });
            } else if (type === 'symbol') {
                paint['icon-opacity'] = Math.abs(+paint['icon-opacity'] || 1) / 2;
                map.addLayer({
                    id,
                    type,
                    filter,
                    layout,
                    paint,
                    source: mapSourceId
                });

                // Change the cursor to a pointer when the mouse is over the layer.
                map.on('mouseenter', id, () => {
                    map.getCanvas().style.cursor = 'pointer';
                });

                // Change it back to a pointer when it leaves.
                map.on('mouseleave', id, () => {
                    map.getCanvas().style.cursor = '';
                });

                map.on('click', id, selectFeature);
            }
        });

        //Map the selected hurricane features
        featureMapping.forEach(feature => {
            const { id, type, filter, style, layout } = feature;
            filter.push(hurricaneSelected);
            if (type === 'symbol') {
                map.addLayer({
                    id,
                    type,
                    filter,
                    layout,
                    paint: style,
                    source: mapSourceId
                });
                // Change the cursor to a pointer when the mouse is over the layer.
                map.on('mouseenter', id, function () {
                    map.getCanvas().style.cursor = 'pointer';
                });

                // Change it back to a pointer when it leaves.
                map.on('mouseleave', id, function () {
                    map.getCanvas().style.cursor = '';
                });

                map.on('click', id, selectFeature);
            } else if (type === 'line' || type === 'fill') {
                map.addLayer({
                    id,
                    type,
                    filter,
                    paint: style,
                    source: mapSourceId
                });
            }
        });
    };

    const selectFeature = (e: EventData) => {
        const { properties, geometry } = e?.features?.[0] || {};
        if (map?.isStyleLoaded() && geometry?.coordinates) {
            const layersToUpdate = map?.getStyle()?.layers?.filter((x: Layer) => x?.source === mapSourceId && flatten(x?.filter).includes('index'));
            updateLayerFilters(layersToUpdate, 'index', properties?.index);
            //different hurricane selected
            const idLayersToUpdate = map?.getStyle()?.layers?.filter((x: Layer) => x?.source === mapSourceId && flatten(x?.filter).includes('id'));
            updateLayerFilters(idLayersToUpdate, 'id', properties?.id);
            if (properties?.id !== currentHurricane?.id) {
                selectHurricane(properties?.id);
            }
            selectHurricaneTrackPoint(e?.features[0]);
            map?.easeTo({ center: geometry?.coordinates });
        }
    };

    // Iterates through all of the layers that have a geojson filter on feature properties such as index or id 
    // and updates the value that those features are filtering on to a new value
    const updateLayerFilters = (layersToUpdate: Layer[], filterField: string, newValue: number) => {
        layersToUpdate?.forEach(x => {
            const filters = cloneDeep(x?.filter);
            const indexFilter = filters?.find(y => typeof y === 'object' && y?.includes(filterField));
            indexFilter[2] = newValue;
            map?.setFilter(x?.id, filters);
        });
    };

    return (
        <div>
            {children}
            {legendColors?.current && renderLegend ? renderLegend(legendColors?.current, legendLabels?.current): null}
        </div>
    );
});
